import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GuestGuard } from '@/guards';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/contexts/ToastContext';
import { BackgroundPattern } from '@/components/auth/BackgroundPattern';
import { LoginForm } from '@/components/auth/LoginForm';
import { ApiError } from '@/api/client';

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const codeOnly = searchParams.get('view') === 'code';
  const previewEmail = searchParams.get('email') ?? 'usuario@exemplo.com';

  const [isLoading, setIsLoading] = useState(false);
  const { requestCode, verifyCode } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const toast = useToast();

  const handleRequestCode = async (email: string) => {
    setIsLoading(true);
    try {
      const ok = await requestCode(email);
      if (ok) {
        toast.success(t('auth.codeSent'));
      } else {
        toast.error(t('auth.loginError'));
      }
      return ok;
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 403) {
        toast.error(error.message || t('auth.waitlistRequired'));
      } else {
        toast.error(t('auth.loginError'));
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (
    email: string,
    code: string
  ): Promise<boolean> => {
    setIsLoading(true);
    try {
      const success = await verifyCode(email, code);
      if (success) {
        if (typeof window !== 'undefined') {
          sessionStorage.setItem('just_logged_in', 'true');
        }
        navigate('/dashboard');
        return true;
      }
      toast.error(t('auth.invalidCredentials'));
      return false;
    } catch (error) {
      if (error instanceof ApiError && error.statusCode === 403) {
        toast.error(error.message || t('auth.waitlistRequired'));
      } else {
        toast.error(t('auth.loginError'));
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GuestGuard>
      <div
        className="min-h-screen flex flex-col relative"
        style={{
          background:
            'linear-gradient(180deg, #ebe4f7 0%, #f3eff8 28%, #faf9fb 55%, #ffffff 100%)',
        }}
      >
        <BackgroundPattern />

        {/* Main content - centered card */}
        <div className="flex-1 flex items-center justify-center p-4 relative z-10">
          <div className="w-full max-w-[440px]">
            <div
              className="rounded-2xl p-5 md:p-6 animate-fade-in"
              style={{
                background: '#ffffff',
                boxShadow:
                  '0 1px 2px rgba(0,0,0,0.04), 0 8px 32px rgba(105,39,218,0.06)',
              }}
            >
              <LoginForm
                onRequestCode={handleRequestCode}
                onVerifyCode={handleVerifyCode}
                isLoading={isLoading}
                initialView={codeOnly ? 'code' : 'options'}
                initialEmail={codeOnly ? previewEmail : ''}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="relative z-10 px-6 md:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-[#9ca3af]">
          <span>© 2026 Paladino</span>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-[#4b5563] transition-colors">
              {t('auth.termsOfUse')}
            </a>
            <a href="#" className="hover:text-[#4b5563] transition-colors">
              {t('auth.privacyPolicy')}
            </a>
          </div>
        </footer>
      </div>
    </GuestGuard>
  );
}
